export interface ProbeDeps {
  // Draw the current video frame and return its pixels, or null if blocked.
  sample: () => Uint8ClampedArray | null
  wait: (ms: number) => Promise<void>
  currentTime: () => number
}

const BLACK_THRESHOLD = 8
const PROBE_WAIT_MS = 300

export function isBlack(pixels: Uint8ClampedArray, threshold = BLACK_THRESHOLD): boolean {
  for (let i = 0; i < pixels.length; i += 4) {
    if (pixels[i] > threshold || pixels[i + 1] > threshold || pixels[i + 2] > threshold) {
      return false
    }
  }
  return true
}

export function pixelsEqual(a: Uint8ClampedArray, b: Uint8ClampedArray): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false
  }
  return true
}

// Resolves false only when we are confident the texture is dead: playback
// advanced yet two samples are both black and identical. Any ambiguity
// (blocked sampling, no advance, any colour or change) resolves true so we
// never downgrade a working player.
export async function probeTexture(deps: ProbeDeps): Promise<boolean> {
  const first = deps.sample()
  const t0 = deps.currentTime()
  await deps.wait(PROBE_WAIT_MS)
  const second = deps.sample()
  const t1 = deps.currentTime()

  if (!first || !second) return true
  if (t1 <= t0) return true
  if (isBlack(first) && isBlack(second) && pixelsEqual(first, second)) return false
  return true
}

// Samples the sphere's actual WebGL output via the surface handle. An earlier
// version read the <video> element through a 2D canvas, but that readback can
// go black on some hardware-decode paths (seen on Chrome 150) even while the
// sphere renders fine — and conversely can succeed while the WebGL texture is
// black. Sampling the rendered frame tests the pipeline that matters.
export interface WebGLProbeSource {
  sampleFramePixels(): Uint8ClampedArray | null
  getVideoElement(): HTMLVideoElement | null
}

export function webglProbeDeps(source: WebGLProbeSource): ProbeDeps {
  return {
    sample: () => source.sampleFramePixels(),
    wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
    // Falls back to 0 if the surface unmounted mid-probe, which reads as "no
    // advance" → inconclusive → no downgrade.
    currentTime: () => source.getVideoElement()?.currentTime ?? 0,
  }
}
